import { createApi } from '@reduxjs/toolkit/query/react';

import { Address } from '@/services/types/address-types';

import { baseQueryWithErrorHandling } from './baseQuery';

export interface LinkedDocumentTemplate {
  id: string;
  name: string;
  category: string;
}

export interface PreDeleteCheckResponse {
  linked_templates: LinkedDocumentTemplate[];
}

export interface Attorney {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  bar_number?: string | null;
  jurisdictions: string[];
  practice_areas: string[];
  title?: string | null;
  notes?: string | null;
  signature?: string | null;
  is_active: boolean;
  firm_id: string;
  address?: Address | null;
}

export interface SetAttorneyAddressPayload {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}

export interface CreateAttorneyPayload {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  bar_number?: string | null;
  jurisdictions?: string[];
  practice_areas?: string[];
  title?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export interface UpdateAttorneyPayload {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  bar_number?: string | null;
  jurisdictions?: string[];
  practice_areas?: string[];
  title?: string | null;
  notes?: string | null;
  is_active?: boolean;
}

export const attorneysApiClient = createApi({
  reducerPath: 'attorneysApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Attorneys'] as const,
  endpoints: (builder) => ({
    listAttorneys: builder.query<Attorney[], { firm_id: string }>({
      query: ({ firm_id }) => `api/firms/${firm_id}/attorneys/`,
      providesTags: (result, error, { firm_id }) => [
        { type: 'Attorneys', id: firm_id },
      ],
    }),
    getAttorney: builder.query<Attorney, { firm_id: string; id: string }>({
      query: ({ firm_id, id }) => `api/firms/${firm_id}/attorneys/${id}/`,
      providesTags: (result, error, { firm_id, id }) => [
        { type: 'Attorneys', id: `${firm_id}:${id}` },
      ],
    }),
    createAttorney: builder.mutation<
      Attorney,
      { firm_id: string; body: CreateAttorneyPayload }
    >({
      query: ({ firm_id, body }) => ({
        url: `api/firms/${firm_id}/attorneys/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (result, error, { firm_id }) => [
        { type: 'Attorneys', id: firm_id },
      ],
    }),
    patchAttorney: builder.mutation<
      Attorney,
      { firm_id: string; id: string; patch: UpdateAttorneyPayload }
    >({
      query: ({ firm_id, id, patch }) => ({
        url: `api/firms/${firm_id}/attorneys/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: (result, error, { firm_id, id }) => [
        { type: 'Attorneys', id: firm_id },
        { type: 'Attorneys', id: `${firm_id}:${id}` },
      ],
    }),
    preDeleteCheck: builder.query<
      PreDeleteCheckResponse,
      { firm_id: string; id: string }
    >({
      query: ({ firm_id, id }) =>
        `api/firms/${firm_id}/attorneys/${id}/pre-delete-check/`,
    }),
    deleteAttorney: builder.mutation<void, { firm_id: string; id: string }>({
      query: ({ firm_id, id }) => ({
        url: `api/firms/${firm_id}/attorneys/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { firm_id }) => [
        { type: 'Attorneys', id: firm_id },
      ],
    }),
    uploadAttorneySignature: builder.mutation<
      Attorney,
      { firm_id: string; id: string; file: File }
    >({
      query: ({ firm_id, id, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `api/firms/${firm_id}/attorneys/${id}/upload_signature/`,
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (result, error, { firm_id, id }) => [
        { type: 'Attorneys', id: firm_id },
        { type: 'Attorneys', id: `${firm_id}:${id}` },
      ],
    }),
    deleteAttorneySignature: builder.mutation<
      void,
      { firm_id: string; id: string }
    >({
      query: ({ firm_id, id }) => ({
        url: `api/firms/${firm_id}/attorneys/${id}/delete_signature/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { firm_id, id }) => [
        { type: 'Attorneys', id: firm_id },
        { type: 'Attorneys', id: `${firm_id}:${id}` },
      ],
    }),
    setAttorneyAddress: builder.mutation<
      Address,
      { firm_id: string; id: string; body: SetAttorneyAddressPayload }
    >({
      query: ({ firm_id, id, body }) => ({
        url: `api/firms/${firm_id}/attorneys/${id}/address/`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, { firm_id, id }) => [
        { type: 'Attorneys', id: firm_id },
        { type: 'Attorneys', id: `${firm_id}:${id}` },
      ],
    }),
  }),
});

export const {
  useListAttorneysQuery,
  useGetAttorneyQuery,
  useCreateAttorneyMutation,
  usePatchAttorneyMutation,
  useLazyPreDeleteCheckQuery,
  useDeleteAttorneyMutation,
  useUploadAttorneySignatureMutation,
  useDeleteAttorneySignatureMutation,
  useSetAttorneyAddressMutation,
} = attorneysApiClient;
